import { type Component, createSignal, For, onMount, Show } from "solid-js";
import InlineConfirmButton from "./InlineConfirmButton";
import { withAccountPassword } from "./lib/accountPassword";
import {
  deletePasskey,
  finishPasskeyModeChange,
  finishPasskeyRegistration,
  getPasskeyStatus,
  type PasskeyStatus,
  preparePasswordless,
  startPasskeyModeChange,
  startPasskeyRegistration,
  startPasswordlessActivation,
} from "./lib/api";
import { token } from "./lib/auth";
import { copyText } from "./lib/clipboard";
import { errorMessage } from "./lib/friendlyApiError";
import { createPasskey, getPasskey, PASSKEYS_UNAVAILABLE, passkeysAvailable } from "./lib/passkeys";

const PasskeySettings: Component = () => {
  const [status, setStatus] = createSignal<PasskeyStatus | null>(null);
  const [password, setPassword] = createSignal("");
  const [name, setName] = createSignal("");
  const [codes, setCodes] = createSignal<string[]>([]);
  const [recoveryToken, setRecoveryToken] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  // #735 — deleting a passkey is irreversible and, in passwordless mode, can
  // lock the account out entirely, so removal goes through the drawer's
  // existing two-tap InlineConfirmButton rather than firing on first tap.
  // One armed id across the whole list: arming a row disarms its siblings.
  const [armedRemoval, setArmedRemoval] = createSignal<string | null>(null);

  // #725 — WebAuthn is either present for this whole page load or it is not
  // (the browser withholds `navigator.credentials` off a secure context), so
  // this is read ONCE at setup rather than made reactive. Every control that
  // needs a ceremony hides behind it and a note names the reason; removal
  // does NOT, because deleting a stored credential runs no ceremony and is
  // the one useful thing left to do here on a plain-http instance.
  const webauthn = passkeysAvailable();

  const currentToken = (): string => {
    const value = token();
    if (value === null) throw new Error("missing auth token");
    return value;
  };
  const reload = async (): Promise<void> => {
    setStatus(await getPasskeyStatus(currentToken()));
  };
  onMount(() => void reload().catch((value) => setError(errorMessage(value))));

  // #729 — FIVE privileged actions here consume the account password, and
  // only one of them (add) used to sit inside the form that visually owned
  // the field. The rationale, and the gate itself, moved to
  // `lib/accountPassword` when #1283 gave the TOTP section its own consumers.
  const gate = { password, setPassword, setBusy, setError };
  const withPassword = (run: (accountPassword: string) => Promise<void>): Promise<void> =>
    withAccountPassword(gate, run);

  const register = async (event: Event): Promise<void> => {
    event.preventDefault();
    await withPassword(async (accountPassword) => {
      const options = await startPasskeyRegistration(currentToken(), accountPassword, name());
      await finishPasskeyRegistration(currentToken(), await createPasskey(options));
      setName("");
      await reload();
    });
  };

  const enableSecondFactor = async (): Promise<void> =>
    withPassword(async (accountPassword) => {
      const options = await startPasskeyModeChange(
        currentToken(),
        accountPassword,
        "second_factor",
      );
      await finishPasskeyModeChange(currentToken(), await getPasskey(options));
      await reload();
    });

  const disablePasskeyLogin = async (): Promise<void> =>
    withPassword(async (accountPassword) => {
      const options = await startPasskeyModeChange(currentToken(), accountPassword, "disabled");
      await finishPasskeyModeChange(currentToken(), await getPasskey(options));
      setCodes([]);
      setRecoveryToken(null);
      await reload();
    });

  const preparePasswordlessMode = async (): Promise<void> =>
    withPassword(async (accountPassword) => {
      const prepared = await preparePasswordless(currentToken(), accountPassword);
      setCodes(prepared.recovery_codes);
      setRecoveryToken(prepared.recovery_token);
    });

  const activatePasswordless = async (): Promise<void> => {
    const pending = recoveryToken();
    if (pending === null) return;
    setBusy(true);
    setError(null);
    try {
      const options = await startPasswordlessActivation(currentToken(), pending);
      await finishPasskeyModeChange(currentToken(), await getPasskey(options));
      setRecoveryToken(null);
      await reload();
    } catch (value) {
      setError(errorMessage(value));
    } finally {
      setBusy(false);
    }
  };

  const copyRecoveryCodes = async (): Promise<void> => {
    setError(null);
    try {
      await copyText(codes().join("\n"));
    } catch (value) {
      setError(errorMessage(value));
    }
  };

  const remove = async (id: string): Promise<void> => {
    await withPassword(async (accountPassword) => {
      await deletePasskey(currentToken(), id, accountPassword);
      await reload();
    });
    setArmedRemoval(null);
  };

  return (
    <div class="settings-section settings-section-card" data-testid="passkey-settings">
      <h4 class="settings-section-heading">passkeys</h4>
      <p>Passkeys belong to this instance domain and cannot be used on another Grappa instance.</p>
      <Show when={status()} keyed>
        {(current) => (
          <>
            <p>Mode: {current.mode}</p>
            {/* #729 — ONE explicitly-labelled re-auth field, above every
                control that consumes it, instead of a field nested in the
                add-passkey form that four sibling buttons read behind the
                user's back. It is cleared after each action (see
                `withPassword`), so each one re-confirms. */}
            <label for="passkey-password">Account password</label>
            <input
              id="passkey-password"
              type="password"
              autocomplete="current-password"
              value={password()}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
            <p role="note">
              Every change below needs your account password. It is cleared after each one.
            </p>
            <Show when={!webauthn}>
              <p role="note" data-testid="passkeys-unavailable">
                {PASSKEYS_UNAVAILABLE} You can still remove passkeys you already registered.
              </p>
            </Show>
            <ul>
              <For each={current.passkeys}>
                {(passkey) => (
                  <li>
                    {passkey.name}{" "}
                    <InlineConfirmButton
                      idleLabel={`remove ${passkey.name}`}
                      confirmLabel={`confirm removing ${passkey.name}`}
                      testId={`passkey-remove-${passkey.id}`}
                      disabled={busy()}
                      armed={armedRemoval() === passkey.id}
                      onArm={() => setArmedRemoval(passkey.id)}
                      onConfirm={() => void remove(passkey.id)}
                    />
                  </li>
                )}
              </For>
            </ul>
            <Show when={webauthn}>
              <form onSubmit={register}>
                <label for="passkey-name">Passkey name</label>
                <input
                  id="passkey-name"
                  autocomplete="off"
                  data-1p-ignore
                  data-bwignore="true"
                  data-lpignore="true"
                  data-protonpass-ignore="true"
                  value={name()}
                  onInput={(e) => setName(e.currentTarget.value)}
                  required
                />
                <button type="submit" disabled={busy()}>
                  add passkey
                </button>
              </form>
            </Show>
            <Show when={webauthn && current.passkeys.length > 0}>
              <button type="button" disabled={busy()} onClick={() => void enableSecondFactor()}>
                require password + passkey
              </button>
              <button
                type="button"
                disabled={busy()}
                onClick={() => void preparePasswordlessMode()}
              >
                use passkey as primary login
              </button>
              <p role="note">
                Passwordless mode disables password and TOTP login. Losing the passkey requires a
                recovery code; without either, only the instance administrator can restore the
                account. Shottino cannot log in.
              </p>
            </Show>
            <Show when={webauthn && current.mode !== "disabled"}>
              <button type="button" disabled={busy()} onClick={() => void disablePasskeyLogin()}>
                return to password login
              </button>
              <p role="note">
                Requires your account password and current passkey. Passwordless recovery codes are
                removed.
              </p>
            </Show>
          </>
        )}
      </Show>
      <Show when={codes().length > 0}>
        <div role="status">
          <p>Save these mandatory recovery codes now. They are shown once.</p>
          <ul>
            <For each={codes()}>
              {(code) => (
                <li>
                  <code>{code}</code>
                </li>
              )}
            </For>
          </ul>
          <button type="button" onClick={() => void copyRecoveryCodes()}>
            Copy all recovery codes
          </button>
          <Show when={recoveryToken() !== null}>
            <button type="button" disabled={busy()} onClick={() => void activatePasswordless()}>
              I saved these codes, activate passwordless
            </button>
            <p>Until this confirmation succeeds, login mode remains unchanged.</p>
          </Show>
        </div>
      </Show>
      <Show when={error()}>{(message) => <p role="alert">{message()}</p>}</Show>
    </div>
  );
};

export default PasskeySettings;
